// Pure helpers for the merge conflict resolver (ENG-539). Our backend conflicts
// are coarse — one per section (visual_config, blueprint) or per asset row —
// each with base/src/dst JSON. Resolution is a per-conflict pick of a side
// ('src' = theirs, 'dst' = ours), mirroring PlayCanvas's click-a-side (minus the
// field-level diff our data can't feed). No React/network here so it's testable;
// the resolver component composes it.

// conflictKey uniquely identifies a conflict in the local choices map — section
// + item id (object sections have no item id). Frontend-only; the backend
// payload carries section + item_id separately (see buildResolutions).
export function conflictKey(c) {
  return `${c.section}::${c.item_id ?? ''}`
}

// Classify a conflict for rendering. Delete-vs-edit conflicts mark the side
// the item is absent from (server sets missing_in_* — merge.go, phase 1).
export function conflictKind(c) {
  if (c.missing_in_src) return 'deleted-in-src'
  if (c.missing_in_dst) return 'deleted-in-dst'
  return 'edit-edit'
}

// buildResolutions turns the choices map into the backend's applyMerge payload:
// [{ section, item_id, choice }] for every conflict that has a chosen side.
export function buildResolutions(conflicts, choices) {
  return (conflicts || [])
    .map((c) => ({ section: c.section, item_id: c.item_id ?? '', choice: choices[conflictKey(c)] }))
    .filter((r) => r.choice === 'src' || r.choice === 'dst')
}

// resolvedCount / allResolved drive the "Resolved X/Y" label and the apply gate.
export function resolvedCount(conflicts, choices) {
  return (conflicts || []).filter((c) => {
    const ch = choices[conflictKey(c)]
    return ch === 'src' || ch === 'dst'
  }).length
}

export function allResolved(conflicts, choices) {
  if (!conflicts || conflicts.length === 0) return true
  return resolvedCount(conflicts, choices) === conflicts.length
}

// pickAll sets every conflict to one side (the bulk "take all source/dest").
export function pickAll(conflicts, side) {
  const out = {}
  for (const c of conflicts || []) out[conflictKey(c)] = side
  return out
}
